import { enumLabel, number, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { UserRole, UserType } from '@repo/shared/types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@repo/ui/collapsible';
import { LanguageSelector } from '@repo/ui/language-selector';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@repo/ui/sidebar';
import {
  BellIcon,
  BookmarkIcon,
  BuildingIcon,
  ChevronDownIcon,
  FilesIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  type LucideIcon,
  PlusIcon,
  SettingsIcon,
  ShieldIcon,
  UsersIcon,
} from 'lucide-react';
import { useLocation } from 'react-router';
import logo from '@/assets/raven.svg';
import { authClient, useSession } from '@/providers/session';
import { trpc } from '@/providers/trpc';
import { GuardLink } from '@/providers/unsaved-changes';
import { useOrganizationStore } from '@/stores/organization-store';

export type AppNavItem = {
  title: string;
  to: string;
  icon: LucideIcon;
  items?: { title: string; to: string }[];
};
export const adminNavItems: AppNavItem[] = [
  {
    get title() {
      return t('workspace.organizations');
    },
    to: '/admin/organizations',
    icon: BuildingIcon,
  },
  {
    get title() {
      return t('workspace.owners');
    },
    to: '/admin/owners',
    icon: ShieldIcon,
  },
];
export const getOrgNavItems = (organizationId: string, role: UserRole): AppNavItem[] => {
  const base = `/o/${organizationId}`;
  return [
    {
      get title() {
        return t('workspace.dashboard');
      },
      to: base,
      icon: LayoutDashboardIcon,
    },
    {
      get title() {
        return t('workspace.clients');
      },
      to: `${base}/clients`,
      icon: UsersIcon,
    },
    {
      get title() {
        return t('workspace.allReports');
      },
      to: `${base}/reports`,
      icon: FilesIcon,
    },
    { title: t('policies.title'), to: `${base}/policies`, icon: SettingsIcon },
    ...(role !== UserRole.ORG_VIEWER
      ? [
          {
            get title() {
              return t('reports.newReport');
            },
            to: `${base}/reports/create`,
            icon: PlusIcon,
          },
        ]
      : []),
    ...([UserRole.ORG_OWNER, UserRole.ORG_MANAGER].some((value) => value === role)
      ? [
          {
            get title() {
              return t('workspace.officers');
            },
            to: `${base}/officers`,
            icon: ShieldIcon,
          },
        ]
      : []),
  ];
};
export const AppSidebar = () => {
  useLocale();

  const { pathname } = useLocation();
  const { user } = useSession();
  const organizationId = /^\/o\/([^/]+)/.exec(pathname)?.[1];
  const organizations = useOrganizationStore((state) => state.organizations);
  const current = useOrganizationStore((state) => state.currentOrganization);
  const role =
    organizations.find((organization) => organization.id === organizationId)
      ?.currentOrganizationRole ??
    current.currentOrganizationRole ??
    UserRole.ORG_VIEWER;
  const isAdmin = pathname.startsWith('/admin') && user.type === UserType.ADMIN;
  const query = trpc.workspace.getDashboard.useQuery(
    { days: 365 },
    { enabled: Boolean(organizationId) && !isAdmin, refetchInterval: 30000 },
  );
  const nav = isAdmin ? adminNavItems : organizationId ? getOrgNavItems(organizationId, role) : [];
  const base = `/o/${organizationId}`;
  const item = (entry: AppNavItem, count?: number) => (
    <SidebarMenuItem key={entry.to}>
      <SidebarMenuButton
        asChild
        isActive={
          pathname === entry.to ||
          (entry.to === `${base}/clients` && pathname.startsWith(`${entry.to}/`)) ||
          (entry.to === `${base}/reports` &&
            pathname.startsWith(`${entry.to}/`) &&
            !pathname.includes('/create'))
        }
      >
        <GuardLink to={entry.to}>
          <entry.icon className="!size-5" />
          <span>{entry.title}</span>
          {count !== undefined && (
            <span className="ms-auto rounded-full bg-[#9290c3] px-2 text-white text-xs">
              {number(count)}
            </span>
          )}
        </GuardLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
  return (
    <Sidebar className="raven-sidebar" collapsible="offcanvas">
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <img alt="" className="size-11" src={logo} />
          <span className="font-bold font-serif text-4xl text-[#222e9c]">
            {t('workspace.raven')}
          </span>
        </div>
        <p className="mt-2 text-xs">{t('workspace.smartKycPlatform')}</p>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          {nav.map((entry) =>
            item(
              entry,
              entry.to === `${base}/clients`
                ? query.data?.allClients
                : entry.to === `${base}/reports`
                  ? query.data?.allReports
                  : undefined,
            ),
          )}
        </SidebarMenu>
        {organizationId && !isAdmin && (
          <Collapsible className="mt-2" defaultOpen={pathname.includes('/settings')} key={base}>
            <SidebarMenu>
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton className="group" isActive={pathname.includes('/settings')}>
                    <SettingsIcon className="!size-5" />
                    <span>{t('workspace.settings')}</span>
                    <ChevronDownIcon className="ms-auto size-4 transition-transform group-data-[state=open]:rotate-180" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenu className="ms-6 mt-2 w-auto border-[#d1cee6] border-s ps-3">
                    {[
                      [t('auth.password'), 'password'],
                      [t('workspace.twoFactorAuthentication'), '2fa'],
                      [t('auth.passkeys'), 'passkeys'],
                      [t('extra.appearance'), 'appearances'],
                      ...([UserRole.ORG_OWNER, UserRole.ORG_MANAGER].some((value) => value === role)
                        ? [[t('workspace.apiTokens'), 'tokens']]
                        : []),
                    ].map(([label, path]) => (
                      <SidebarMenuItem key={path}>
                        <SidebarMenuButton
                          asChild
                          className="!text-xs !min-h-9"
                          isActive={pathname === `${base}/settings/${path}`}
                        >
                          <GuardLink to={`${base}/settings/${path}`}>{label}</GuardLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </CollapsibleContent>
              </SidebarMenuItem>
            </SidebarMenu>
          </Collapsible>
        )}
      </SidebarContent>
      <SidebarFooter>
        <LanguageSelector />
        {organizationId && !isAdmin && (
          <SidebarMenu>
            {item(
              {
                get title() {
                  return t('workspace.bookmarks');
                },
                to: `${base}/bookmarks`,
                icon: BookmarkIcon,
              },
              query.data?.bookmarks,
            )}
            {item(
              {
                get title() {
                  return t('workspace.notifications');
                },
                to: `${base}/notifications`,
                icon: BellIcon,
              },
              query.data?.unread,
            )}
          </SidebarMenu>
        )}
        <div className="my-3 flex items-center gap-3 border-[#d1cee6] border-y py-4">
          <span className="flex size-12 items-center justify-center rounded-full bg-gradient-to-br from-[#ad93af] to-[#51467b] text-sm text-white">
            {user.firstName?.[0]}
            {user.lastName?.[0]}
          </span>
          <div>
            <p className="font-serif text-[#202f92] text-base">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs">{enumLabel(role)}</p>
          </div>
        </div>
        <SidebarMenuButton onClick={() => authClient.signOut()}>
          <LogOutIcon />
          {t('workspace.logOut')}{' '}
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
};
